import React, { useCallback, useMemo, useRef, useState } from 'react';

import { Segmented } from 'antd';

import alertCaller from '@core/app/actions/alert-caller';
import progressCaller from '@core/app/actions/progress-caller';
import alertConstants from '@core/app/constants/alert-constants';
import { promarkPnPPoints } from '@core/app/constants/fisheyeCameraConstants';
import { getWorkarea } from '@core/app/constants/workarea-constants';
import { cameraCalibrationApi } from '@core/helpers/api/camera-calibration';
import checkDeviceStatus from '@core/helpers/check-device-status';
import { loadCameraCalibrationTask } from '@core/helpers/device/promark/calibration';
import promarkDataStore from '@core/helpers/device/promark/promark-data-store';
import deviceMaster from '@core/helpers/device-master';
import useI18n from '@core/helpers/useI18n';
import type { FisheyeCameraParametersV3, FisheyeCameraParametersV3Cali } from '@core/interfaces/FisheyePreview';
import type { IDeviceInfo } from '@core/interfaces/IDevice';

import styles from './Calibration.module.scss';
import Calibration from './common/Calibration';
import CheckPnP from './common/CheckPnP';
import downloadCalibrationFile from './common/downloadCalibrationFile';
import Instruction from './common/Instruction';
import SolvePnP from './common/SolvePnP';
import Title from './common/Title';
import type { RenderWrapper } from './common/types';

/* eslint-disable perfectionist/sort-enums */
enum Steps {
  PRE_CHESSBOARD = 1,
  CALIBRATION = 2,
  PUT_PAPER = 3,
  SOLVE_PNP_INSTRUCTION = 4,
  SOLVE_PNP = 5,
  CHECK_PNP = 6,
}
/* eslint-enable perfectionist/sort-enums */

interface Props {
  currentData?: FisheyeCameraParametersV3;
  device: IDeviceInfo;
  onBack?: () => void;
  onClose: (completed?: boolean) => void;
  renderWrapper?: RenderWrapper;
}

const PROGRESS_ID = 'promark-calibration';
const PromarkCalibration = ({
  currentData,
  device: { model, serial },
  onBack,
  onClose,
  renderWrapper,
}: Props): React.JSX.Element => {
  const lang = useI18n();
  const tCali = lang.calibration;
  // should be the same though, just make it look better
  const { height: workareaHeight, width: workareaWidth } = useMemo(() => getWorkarea(model), [model]);
  const calibratingParam = useRef<FisheyeCameraParametersV3Cali>({});
  const [withSafe, setWithSafe] = useState(false);
  const [steps, setSteps] = useState<Steps[]>([Steps.PRE_CHESSBOARD]);
  const updateParam = useCallback((param: FisheyeCameraParametersV3Cali) => {
    calibratingParam.current = { ...calibratingParam.current, ...param };
  }, []);
  const step = useMemo(() => steps[steps.length - 1], [steps]);
  const setStep = useCallback((newStep: Steps) => setSteps((prev) => [...prev, newStep]), []);
  const handleStepBack = useCallback(() => {
    const newSteps = [...steps];

    newSteps.pop();

    if (newSteps.length === 0) {
      (onBack ?? onClose)();

      return;
    }

    setSteps(newSteps);
  }, [steps, onBack, onClose]);

  if (step === Steps.PRE_CHESSBOARD) {
    const handleDownloadChessboard = () => {
      downloadCalibrationFile(
        withSafe ? 'assets/charuco-15-10.pdf' : 'assets/promark-chessboard.pdf',
        withSafe ? tCali.download_calibration_pattern : tCali.download_chessboard_file,
        withSafe ? 'Calibration Pattern' : 'Chessboard',
      );
    };

    return (
      <Instruction
        animationSrcs={
          withSafe
            ? [
                { src: 'video/promark-calibration/1-charuco.webm', type: 'video/webm' },
                { src: 'video/promark-calibration/1-charuco.mp4', type: 'video/mp4' },
              ]
            : [
                { src: 'video/promark-calibration/1-chessboard.webm', type: 'video/webm' },
                { src: 'video/promark-calibration/1-chessboard.mp4', type: 'video/mp4' },
              ]
        }
        buttons={[
          { label: tCali.back, onClick: handleStepBack },
          currentData && {
            label: tCali.skip,
            onClick: async () => {
              await cameraCalibrationApi.updateData(currentData);
              updateParam(currentData);
              setStep(Steps.PUT_PAPER);
            },
          },
          {
            label: tCali.next,
            onClick: () => setStep(Steps.CALIBRATION),
            type: 'primary' as const,
          },
        ].filter(Boolean)}
        contentBeforeSteps={
          <div className={styles.tab}>
            <Segmented
              block
              onChange={(v) => setWithSafe(v === 1)}
              options={[
                { label: tCali.without_promark_safe_plus, value: 0 },
                { label: tCali.with_promark_safe_plus, value: 1 },
              ]}
            />
          </div>
        }
        onClose={onClose}
        renderWrapper={renderWrapper}
        steps={
          withSafe
            ? [tCali.put_charuco_promark_desc_1, tCali.put_charuco_promark_desc_2]
            : [tCali.put_chessboard_promark_desc_1, tCali.put_chessboard_promark_desc_2]
        }
        title={<Title link={tCali.promark_help_link} title={tCali.put_charuco} />}
      >
        <div className={styles.link} onClick={handleDownloadChessboard}>
          {withSafe ? tCali.download_calibration_pattern : tCali.download_chessboard_file}
        </div>
      </Instruction>
    );
  }

  if (step === Steps.CALIBRATION) {
    return (
      <Calibration
        cameraOptions={{ source: 'usb' }}
        charuco={[15, 10]}
        chessboard={[18, 18]}
        description={[
          tCali.put_chessboard_promark_1,
          withSafe ? tCali.put_charuco_promark_2 : tCali.put_chessboard_promark_2,
        ]}
        onBack={handleStepBack}
        onClose={onClose}
        onNext={() => setStep(Steps.PUT_PAPER)}
        renderWrapper={renderWrapper}
        title={<Title link={tCali.promark_help_link} title={tCali.camera_calibration} />}
        updateParam={updateParam}
      />
    );
  }

  if (step === Steps.PUT_PAPER) {
    const handleNext = async (skip = false) => {
      const deviceStatus = await checkDeviceStatus(deviceMaster.currentDevice!.info);

      if (!deviceStatus) {
        return;
      }

      try {
        if (!skip) {
          progressCaller.openNonstopProgress({
            id: PROGRESS_ID,
            message: tCali.drawing_calibration_image,
          });
          await loadCameraCalibrationTask(model, workareaWidth);
          await deviceMaster.doPromarkCalibration();
        }

        setStep(Steps.SOLVE_PNP_INSTRUCTION);
      } catch (err) {
        console.error(err);

        if (err && (err as any)[1] === 'DOOR_OPENED') {
          await deviceMaster.stop();
          alertCaller.popUp({
            buttonType: alertConstants.RETRY_CANCEL,
            id: 'DOOR_OPENED',
            message: lang.monitor.HARDWARE_ERROR_DOOR_OPENED,
            onRetry: handleNext,
            type: alertConstants.SHOW_POPUP_ERROR,
          });
        }
      } finally {
        progressCaller.popById(PROGRESS_ID);
      }
    };

    return (
      <Instruction
        animationSrcs={
          withSafe
            ? [
                { src: 'video/promark-calibration/2-cut-with-safe.webm', type: 'video/webm' },
                { src: 'video/promark-calibration/2-cut-with-safe.mp4', type: 'video/mp4' },
              ]
            : [
                { src: 'video/promark-calibration/2-cut.webm', type: 'video/webm' },
                { src: 'video/promark-calibration/2-cut.mp4', type: 'video/mp4' },
              ]
        }
        buttons={[
          {
            label: tCali.back,
            onClick: handleStepBack,
          },
          { label: tCali.skip, onClick: () => handleNext(true) },
          { label: tCali.start_engrave, onClick: () => handleNext(), type: 'primary' },
        ]}
        onClose={() => onClose(false)}
        renderWrapper={renderWrapper}
        steps={[tCali.put_paper_promark_1, tCali.put_paper_promark_2]}
        title={<Title link={tCali.promark_help_link} title={tCali.put_paper_promark} />}
      />
    );
  }

  if (step === Steps.SOLVE_PNP_INSTRUCTION) {
    return (
      <Instruction
        animationSrcs={[
          { src: 'video/promark-calibration/3-align.webm', type: 'video/webm' },
          { src: 'video/promark-calibration/3-align.mp4', type: 'video/mp4' },
        ]}
        buttons={[
          { label: tCali.back, onClick: handleStepBack },
          { label: tCali.next, onClick: () => setStep(Steps.SOLVE_PNP), type: 'primary' },
        ]}
        onClose={() => onClose(false)}
        renderWrapper={renderWrapper}
        steps={[tCali.solve_pnp_step1, tCali.solve_pnp_step2]}
        title={tCali.solve_pnp_title}
      />
    );
  }

  if (step === Steps.SOLVE_PNP) {
    return (
      <SolvePnP
        dh={0}
        hasNext
        imgSource="usb"
        onBack={handleStepBack}
        onClose={onClose}
        onNext={async (rvec, tvec) => {
          progressCaller.openNonstopProgress({ id: PROGRESS_ID, message: lang.device.processing });
          updateParam({ rvec, tvec });
          console.log('calibratingParam.current', calibratingParam.current);
          progressCaller.popById(PROGRESS_ID);

          setStep(Steps.CHECK_PNP);
        }}
        params={calibratingParam.current}
        refPoints={promarkPnPPoints[workareaWidth]}
        renderWrapper={renderWrapper}
        titleLink={tCali.promark_help_link}
      />
    );
  }

  if (step === Steps.CHECK_PNP) {
    const params: FisheyeCameraParametersV3 = {
      d: calibratingParam.current.d!,
      k: calibratingParam.current.k!,
      rvec: calibratingParam.current.rvec!,
      tvec: calibratingParam.current.tvec!,
      v: 3,
    };

    return (
      <CheckPnP
        cameraOptions={{ source: 'usb' }}
        dh={0}
        grid={{ x: [0, workareaWidth, 10], y: [0, workareaHeight, 10] }}
        onBack={handleStepBack}
        onClose={onClose}
        onNext={async () => {
          promarkDataStore.set(serial, 'cameraParameters', params);
          alertCaller.popUp({ message: tCali.camera_parameter_saved_successfully });
          onClose(true);
        }}
        params={params}
        points={promarkPnPPoints[workareaWidth]}
        renderWrapper={renderWrapper}
      />
    );
  }

  onClose();

  return <></>;
};

export default PromarkCalibration;
